import { useState, useCallback, useMemo } from 'react';
import { Menu, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useSidebar } from '../../context/SidebarContext';
import { useCourseData } from './hooks/useCourseData';
import { CourseBrowser } from './components/CourseBrowser';
import { CourseDetail } from './components/CourseDetail';
import { LessonView } from './components/LessonView';
import { FreeformView } from './components/FreeformView';
import type { ViewState, LessonProgressEntry } from './types';

interface CourseListItem {
  courseId: string;
  slug: string;
  title: string;
  description: string | null;
  difficultyLevel: string;
  estimatedDurationHours: number | null;
  tags: string[];
  status: string;
}

interface CurriculumLesson {
  lessonId: string;
  title: string;
  lessonType: string;
  sequenceOrder: number;
  estimatedDurationMinutes: number | null;
}

interface CurriculumModule {
  moduleId: string;
  title: string;
  description: string | null;
  sequenceOrder: number;
  estimatedDurationMinutes: number | null;
  lessons: CurriculumLesson[];
}

interface Curriculum extends CourseListItem {
  modules: CurriculumModule[];
}

export function LearnAnythingContent() {
  const { isCollapsed, toggle } = useSidebar();
  const { courses, coursesLoading, fetchCurriculum, fetchProgress, enrollCourse } = useCourseData();

  const [viewState, setViewState] = useState<ViewState>('browse');
  const [curriculum, setCurriculum] = useState<Curriculum | null>(null);
  const [selectedModule, setSelectedModule] = useState<CurriculumModule | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<CurriculumLesson | null>(null);
  const [lessonProgress, setLessonProgress] = useState<Record<string, LessonProgressEntry>>({});
  // Track whether enrollment has been triggered for the current course
  const [enrolledSlug, setEnrolledSlug] = useState<string | null>(null);

  const loadProgress = useCallback(
    async (slug: string) => {
      const progress = await fetchProgress(slug);
      setLessonProgress(progress);
    },
    [fetchProgress],
  );

  const handleSelectCourse = useCallback(
    async (course: CourseListItem) => {
      const curr = await fetchCurriculum(course.slug);
      if (curr) {
        setCurriculum(curr);
        loadProgress(course.slug);
        setViewState('course');
      }
    },
    [fetchCurriculum, loadProgress],
  );

  const handleStartLesson = (lesson: CurriculumLesson, module: CurriculumModule) => {
    setSelectedLesson(lesson);
    setSelectedModule(module);
    setViewState('lesson');
  };

  const handleBackToBrowse = () => {
    setViewState('browse');
    setCurriculum(null);
    setSelectedLesson(null);
    setSelectedModule(null);
    setLessonProgress({});
    setEnrolledSlug(null);
  };

  const handleBackToCourse = () => {
    setViewState('course');
    setSelectedLesson(null);
    setSelectedModule(null);
    if (curriculum) {
      loadProgress(curriculum.slug);
    }
  };

  // Enrollment guard: only enroll once per course session
  const handleEnroll = useCallback(() => {
    if (!curriculum || enrolledSlug === curriculum.slug) return;
    setEnrolledSlug(curriculum.slug);
    enrollCourse(curriculum.slug);
  }, [curriculum, enrolledSlug, enrollCourse]);

  const handleNextLesson = () => {
    if (!curriculum || !selectedModule || !selectedLesson) return;

    const moduleIndex = curriculum.modules.findIndex((m) => m.moduleId === selectedModule.moduleId);
    const lessonIndex = selectedModule.lessons.findIndex(
      (l) => l.lessonId === selectedLesson.lessonId,
    );

    if (lessonIndex < selectedModule.lessons.length - 1) {
      setSelectedLesson(selectedModule.lessons[lessonIndex + 1]);
      return;
    }

    if (moduleIndex < curriculum.modules.length - 1) {
      const nextModule = curriculum.modules[moduleIndex + 1];
      setSelectedModule(nextModule);
      setSelectedLesson(nextModule.lessons[0]);
    }
  };

  const handlePrevLesson = () => {
    if (!curriculum || !selectedModule || !selectedLesson) return;

    const moduleIndex = curriculum.modules.findIndex((m) => m.moduleId === selectedModule.moduleId);
    const lessonIndex = selectedModule.lessons.findIndex(
      (l) => l.lessonId === selectedLesson.lessonId,
    );

    if (lessonIndex > 0) {
      setSelectedLesson(selectedModule.lessons[lessonIndex - 1]);
      return;
    }

    if (moduleIndex > 0) {
      const prevModule = curriculum.modules[moduleIndex - 1];
      setSelectedModule(prevModule);
      setSelectedLesson(prevModule.lessons[prevModule.lessons.length - 1]);
    }
  };

  const hasNextLesson = useMemo(() => {
    if (!curriculum || !selectedModule || !selectedLesson) return false;
    const moduleIndex = curriculum.modules.findIndex((m) => m.moduleId === selectedModule.moduleId);
    const lessonIndex = selectedModule.lessons.findIndex(
      (l) => l.lessonId === selectedLesson.lessonId,
    );
    return (
      lessonIndex < selectedModule.lessons.length - 1 || moduleIndex < curriculum.modules.length - 1
    );
  }, [curriculum, selectedModule, selectedLesson]);

  const hasPrevLesson = useMemo(() => {
    if (!curriculum || !selectedModule || !selectedLesson) return false;
    const moduleIndex = curriculum.modules.findIndex((m) => m.moduleId === selectedModule.moduleId);
    const lessonIndex = selectedModule.lessons.findIndex(
      (l) => l.lessonId === selectedLesson.lessonId,
    );
    return lessonIndex > 0 || moduleIndex > 0;
  }, [curriculum, selectedModule, selectedLesson]);

  // Render based on view state
  if (viewState === 'freeform') {
    return <FreeformView onBack={handleBackToBrowse} />;
  }

  if (viewState === 'lesson' && curriculum && selectedModule && selectedLesson) {
    return (
      <LessonView
        course={curriculum}
        module={selectedModule}
        lesson={selectedLesson}
        onBack={handleBackToCourse}
        onNextLesson={handleNextLesson}
        onPrevLesson={handlePrevLesson}
        hasNext={hasNextLesson}
        hasPrev={hasPrevLesson}
        initialProgress={lessonProgress[selectedLesson.lessonId]}
        onEnroll={handleEnroll}
      />
    );
  }

  if (viewState === 'course' && curriculum) {
    return (
      <CourseDetail
        curriculum={curriculum}
        onBack={handleBackToBrowse}
        onStartLesson={handleStartLesson}
        lessonProgress={lessonProgress}
      />
    );
  }

  // Default: browse view
  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="px-4 py-3 flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={toggle}
            className={cn(
              'h-11 w-11 flex-shrink-0 bg-gray-100 dark:bg-zinc-700',
              !isCollapsed && 'invisible',
            )}
            title="Open sidebar"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <GraduationCap className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">Learn Anything</h1>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <CourseBrowser
          courses={courses}
          loading={coursesLoading}
          onSelectCourse={handleSelectCourse}
          onStartFreeform={() => setViewState('freeform')}
        />
      </div>
    </div>
  );
}
